import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Search, Plus, RefreshCw, ChevronDown, MoreVertical,
  Eye, Edit2, Trash2, X, Loader2, Users, Building2,
  Filter, Phone, Mail, MapPin, Star, ToggleLeft, ToggleRight,
  ArrowUpDown, ChevronUp, IndianRupee, TrendingUp, Gauge, Package,
} from 'lucide-react';
import toast from 'react-hot-toast';
import supplierService from '../../../../services/supplierService';
import branchService from '../../../../services/branchService';
import { useAuth } from '../../../../context/AuthContext';
import { ConfirmDialog } from '@/components/Admin/Menu_Management/UIComponents';
import SupplierForm from './SupplierForm';
import SupplierDetail from './SupplierDetail';
import { P } from '@/config/permissionCodes';

const STATUS_OPTIONS = [
  { value: 'all',      label: 'All Suppliers' },
  { value: 'active',   label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const PAYMENT_BADGE = {
  COD:      'bg-amber-100 text-amber-700',
  'Net 7':  'bg-blue-100 text-blue-700',
  'Net 10': 'bg-sky-100 text-sky-700',
  'Net 15': 'bg-teal-100 text-teal-700',
  'Net 30': 'bg-violet-100 text-violet-700',
  Advance:  'bg-red-100 text-red-700',
};

// ── Access Denied ─────────────────────────────────────────────────────────────
function AccessDenied() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
        <p className="text-sm text-slate-500">You don't have permission to view Supplier Management.</p>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SupplierList() {
  const { user, hasPermission } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';

  // ── Permissions ──────────────────────────────────────────────────────────────
  const canViewScreen = hasPermission(P.INVENTORY.SUPPLIER.SCREEN);
  const canAdd        = hasPermission(P.INVENTORY.SUPPLIER.ADD);
  const canEdit       = hasPermission(P.INVENTORY.SUPPLIER.EDIT);
  const canDeactivate = hasPermission(P.INVENTORY.SUPPLIER.DEACTIVATE);
  const canDelete     = hasPermission(P.INVENTORY.SUPPLIER.DELETE);
  const hasAnyRowAction = canEdit || canDeactivate || canDelete;

  if (!canViewScreen) return <AccessDenied />;

  // ── Branch ───────────────────────────────────────────────────────────────────
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const branchRef = useRef(null);

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery]       = useState('');
  const [statusFilter, setStatusFilter]     = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // ── Sort ─────────────────────────────────────────────────────────────────────
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // ── Dropdowns ────────────────────────────────────────────────────────────────
  const [showStatusDropdown, setShowStatusDropdown]     = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [openMenuId, setOpenMenuId]                     = useState(null);
  const statusRef   = useRef(null);
  const categoryRef = useRef(null);

  // ── Modals ───────────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen]             = useState(false);
  const [detailOpen, setDetailOpen]         = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [confirmDialog, setConfirmDialog]   = useState({
    isOpen: false, title: '', message: '', onConfirm: null,
  });

  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  // ── Fetch Data ────────────────────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [suppData, statsData] = await Promise.all([
        supplierService.getAll(selectedBranchId ? { branchId: selectedBranchId } : {}),
        supplierService.getStats(),
      ]);
      const arr = Array.isArray(suppData?.data) ? suppData.data : Array.isArray(suppData) ? suppData : [];
      setSuppliers(arr);
      setStats(statsData?.data || statsData);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
      toast.error('Failed to load suppliers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedBranchId]);

  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches()
      .then((result) => { if (result?.length > 1) setBranches(result); })
      .catch(console.error);
  }, [isOwner]);

  // ── Click-outside close ───────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (statusRef.current   && !statusRef.current.contains(e.target))   setShowStatusDropdown(false);
      if (categoryRef.current && !categoryRef.current.contains(e.target)) setShowCategoryDropdown(false);
      if (branchRef.current   && !branchRef.current.contains(e.target))   setShowBranchDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Sorting ───────────────────────────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown size={14} className="opacity-40 group-hover:opacity-70" />;
    return sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // ── Client-side filtering & sorting ──────────────────────────────────────────
  const filteredSuppliers = useMemo(() => {
    let result = [...suppliers];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          s.contactPerson?.toLowerCase().includes(q) ||
          s.phone?.includes(q) ||
          s.email?.toLowerCase().includes(q) ||
          s.city?.toLowerCase().includes(q)
      );
    }
    if (statusFilter === 'active')   result = result.filter((s) => s.isActive);
    if (statusFilter === 'inactive') result = result.filter((s) => !s.isActive);
    if (categoryFilter !== 'all')    result = result.filter((s) => s.category === categoryFilter);

    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = sortConfig.key === 'performanceScore' ? getWeightedScore(a) : a[sortConfig.key];
        let bVal = sortConfig.key === 'performanceScore' ? getWeightedScore(b) : b[sortConfig.key];
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (!isNaN(aNum) && !isNaN(bNum) && aVal !== '' && bVal !== '') {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        const cmp = String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [suppliers, searchQuery, statusFilter, categoryFilter, sortConfig]);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const getWeightedScore = (s) => {
    if (!s.performance) return 0;
    const p = s.performance;
    return Number((p.quality * 0.4 + p.delivery * 0.3 + p.cost * 0.2 + p.service * 0.1).toFixed(1));
  };

  const getScoreColor = (score) => {
    if (score >= 4.5) return 'text-green-600';
    if (score >= 4.0) return 'text-teal-600';
    if (score >= 3.5) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 4.5) return 'bg-green-50';
    if (score >= 4.0) return 'bg-teal-50';
    if (score >= 3.5) return 'bg-amber-50';
    return 'bg-red-50';
  };

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleAdd  = () => { if (!canAdd) return; setSelectedSupplier(null); setFormOpen(true); };
  const handleView = (s) => { setSelectedSupplier(s); setDetailOpen(true); setOpenMenuId(null); };
  const handleEdit = (s) => { if (!canEdit) return; setSelectedSupplier(s); setFormOpen(true); setOpenMenuId(null); };

  const handleToggleStatus = async (s) => {
    if (!canDeactivate) return;
    try {
      await supplierService.toggleStatus(s.id);
      toast.success(`${s.name} ${s.isActive ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch { toast.error('Failed to update status'); }
    setOpenMenuId(null);
  };

  const handleDelete = (s) => {
    if (!canDelete) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Supplier',
      message: `Delete "${s.name}"? This cannot be undone. Any linked purchase orders will remain.`,
      onConfirm: async () => {
        try {
          await supplierService.delete(s.id);
          toast.success('Supplier deleted');
          fetchData();
        } catch { toast.error('Failed to delete supplier'); }
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
      },
    });
    setOpenMenuId(null);
  };

  const handleFormSave = () => { setFormOpen(false); setSelectedSupplier(null); fetchData(); };

  const clearFilters = () => { setSearchQuery(''); setStatusFilter('all'); setCategoryFilter('all'); };
  const hasActiveFilters = searchQuery || statusFilter !== 'all' || categoryFilter !== 'all';

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 sm:p-6 max-w-full">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-6 h-6 text-teal-600" />
            Supplier Management
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage your supplier network, track performance, and monitor spending
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isOwner && branches.length > 1 && (
            <div className="relative" ref={branchRef}>
              <button
                onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Building2 className="w-3.5 h-3.5 text-slate-500" />
                {selectedBranchId ? branches.find((b) => b.id === selectedBranchId)?.name || 'Branch' : 'All Branches'}
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>
              {showBranchDropdown && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                  <button
                    onClick={() => { setSelectedBranchId(null); setShowBranchDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${!selectedBranchId ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
                  >
                    All Branches
                  </button>
                  {branches.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => { setSelectedBranchId(b.id); setShowBranchDropdown(false); }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${selectedBranchId === b.id ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Add Supplier — only if permitted */}
          {canAdd && (
            <button
              onClick={handleAdd}
              className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Add Supplier
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <KpiCard icon={Users}       label="Total Suppliers"  value={stats.total}  sub={`${stats.active} active, ${stats.inactive} inactive`} color="teal" />
          <KpiCard icon={TrendingUp}  label="Active Suppliers" value={stats.active} sub={`${stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}% of total`} color="green" />
          <KpiCard icon={IndianRupee} label="Total Order Value" value={`₹${(stats.totalOrderValue || 0).toLocaleString('en-IN')}`} sub={`Across ${stats.total} suppliers`} color="blue" />
          <KpiCard icon={Gauge}       label="Avg Rating"       value={stats.avgRating || '—'} sub={`${stats.categoriesCount || 0} categories`} color="amber" />
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search name, phone, city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
          />
        </div>

        {/* Status Dropdown */}
        <div className="relative" ref={statusRef}>
          <button
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            {STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label || 'Status'}
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </button>
          {showStatusDropdown && (
            <div className="absolute left-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { setStatusFilter(opt.value); setShowStatusDropdown(false); }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${statusFilter === opt.value ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category Dropdown */}
        <div className="relative" ref={categoryRef}>
          <button
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            {categoryFilter === 'all' ? 'All Categories' : categoryFilter}
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </button>
          {showCategoryDropdown && (
            <div className="absolute left-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1 max-h-52 overflow-y-auto">
              <button
                onClick={() => { setCategoryFilter('all'); setShowCategoryDropdown(false); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${categoryFilter === 'all' ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
              >
                All Categories
              </button>
              {supplierService.categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setCategoryFilter(cat); setShowCategoryDropdown(false); }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${categoryFilter === cat ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        <button onClick={fetchData} className="p-2 text-slate-400 hover:text-teal-600 hover:bg-slate-50 rounded-lg transition-colors" title="Refresh">
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
        {hasActiveFilters && (
          <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
            <span className="ml-2 text-sm text-slate-500">Loading suppliers...</span>
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-sm font-semibold text-slate-600 mb-1">
              {hasActiveFilters ? 'No matching suppliers' : 'No suppliers yet'}
            </h3>
            <p className="text-xs text-slate-400 mb-4 max-w-xs">
              {hasActiveFilters
                ? "Try adjusting your search or filters to find what you're looking for"
                : 'Add your first supplier to start managing your supply chain'}
            </p>
            {!hasActiveFilters && canAdd && (
              <button
                onClick={handleAdd}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Supplier
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th onClick={() => handleSort('name')} className={`text-left px-4 py-3 font-semibold uppercase tracking-wide cursor-pointer select-none ${sortConfig.key === 'name' ? 'text-teal-600' : 'text-slate-600'}`}>
                    <div className="flex items-center gap-1 group">Supplier {getSortIcon('name')}</div>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Contact</th>
                  <th onClick={() => handleSort('category')} className={`text-left px-4 py-3 font-semibold uppercase tracking-wide cursor-pointer select-none ${sortConfig.key === 'category' ? 'text-teal-600' : 'text-slate-600'}`}>
                    <div className="flex items-center gap-1 group">Category {getSortIcon('category')}</div>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Payment</th>
                  <th onClick={() => handleSort('totalOrders')} className={`text-center px-4 py-3 font-semibold uppercase tracking-wide cursor-pointer select-none ${sortConfig.key === 'totalOrders' ? 'text-teal-600' : 'text-slate-600'}`}>
                    <div className="flex items-center justify-center gap-1 group">Orders {getSortIcon('totalOrders')}</div>
                  </th>
                  <th onClick={() => handleSort('totalValue')} className={`text-right px-4 py-3 font-semibold uppercase tracking-wide cursor-pointer select-none ${sortConfig.key === 'totalValue' ? 'text-teal-600' : 'text-slate-600'}`}>
                    <div className="flex items-center justify-end gap-1 group">Value (₹) {getSortIcon('totalValue')}</div>
                  </th>
                  <th onClick={() => handleSort('performanceScore')} className={`text-center px-4 py-3 font-semibold uppercase tracking-wide cursor-pointer select-none ${sortConfig.key === 'performanceScore' ? 'text-teal-600' : 'text-slate-600'}`}>
                    <div className="flex items-center justify-center gap-1 group">Score {getSortIcon('performanceScore')}</div>
                  </th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                  {/* Actions column — only render if user has at least one row action */}
                  {hasAnyRowAction && (
                    <th className="text-center px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide w-12" />
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSuppliers.map((s) => {
                  const score = getWeightedScore(s);
                  return (
                    <tr key={s.id} className="hover:bg-slate-50/60 transition-colors group">

                      {/* Supplier name */}
                      <td className="px-4 py-3">
                        <button onClick={() => handleView(s)} className="text-left hover:opacity-80">
                          <p className="font-semibold text-slate-800 group-hover:text-teal-600 transition-colors">{s.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {s.contactPerson && <span className="text-[11px] text-slate-400">{s.contactPerson}</span>}
                            {s.city && (
                              <span className="flex items-center gap-0.5 text-[11px] text-slate-400">
                                <MapPin className="w-2.5 h-2.5" />{s.city}
                              </span>
                            )}
                          </div>
                        </button>
                      </td>

                      {/* Contact */}
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1 text-slate-600">
                            <Phone className="w-3 h-3 text-slate-400" />
                            {s.phone}
                          </div>
                          {s.email && (
                            <div className="flex items-center gap-1 text-slate-500 truncate max-w-[180px]">
                              <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
                              <span className="truncate">{s.email}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Category */}
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">
                          {s.category || '—'}
                        </span>
                      </td>

                      {/* Payment Terms */}
                      <td className="px-4 py-3">
                        {s.paymentTerms ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${PAYMENT_BADGE[s.paymentTerms] || 'bg-slate-100 text-slate-600'}`}>
                            {s.paymentTerms}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Orders */}
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <Package className="w-3 h-3 text-slate-400" />
                          {s.totalOrders || 0}
                        </span>
                      </td>

                      {/* Value */}
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">
                        ₹{(s.totalValue || 0).toLocaleString('en-IN')}
                      </td>

                      {/* Performance Score */}
                      <td className="px-4 py-3 text-center">
                        {score > 0 ? (
                          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${getScoreBg(score)} ${getScoreColor(score)}`}>
                            <Star className="w-3 h-3 fill-current" />
                            {score}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-[11px]">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          s.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                        }`}>
                          {s.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Actions — only rendered if user has at least one action */}
                      {hasAnyRowAction && (
  <td className="px-4 py-3 text-center">
    <div className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (openMenuId === s.id) {
            setOpenMenuId(null);
          } else {
            const rect = e.currentTarget.getBoundingClientRect();
            setMenuPosition({
              top:   rect.bottom + window.scrollY + 4,
              right: window.innerWidth - rect.right,
            });
            setOpenMenuId(s.id);
          }
        }}
        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
                            </button>

                            {/* Supplier Action Menu Portal */}
{openMenuId && (() => {
  const s = filteredSuppliers.find((sup) => sup.id === openMenuId);
  if (!s) return null;
  return (
    <div
      style={{ position: 'fixed', top: menuPosition.top, right: menuPosition.right, zIndex: 50 }}
      className="w-44 bg-white border border-slate-200 rounded-lg shadow py-1"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => handleView(s)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
      >
        <Eye className="w-3.5 h-3.5" /> View Details
      </button>

      {canEdit && (
        <button
          onClick={() => handleEdit(s)}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
        >
          <Edit2 className="w-3.5 h-3.5" /> Edit
        </button>
      )}

      {canDeactivate && (
        <button
          onClick={() => handleToggleStatus(s)}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
        >
          {s.isActive
            ? <ToggleLeft className="w-3.5 h-3.5" />
            : <ToggleRight className="w-3.5 h-3.5" />}
          {s.isActive ? 'Deactivate' : 'Activate'}
        </button>
      )}

      {canDelete && (
        <>
          <div className="border-t border-slate-100 my-1" />
          <button
            onClick={() => handleDelete(s)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </>
      )}
    </div>
  );
})()}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && filteredSuppliers.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-500 flex items-center justify-between">
            <span>Showing {filteredSuppliers.length} of {suppliers.length} suppliers</span>
            {stats && (
              <span className="text-slate-400">
                Total network value: ₹{(stats.totalOrderValue || 0).toLocaleString('en-IN')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {formOpen && (canAdd || canEdit) && (
        <SupplierForm
          supplier={selectedSupplier}
          onClose={() => { setFormOpen(false); setSelectedSupplier(null); }}
          onSave={handleFormSave}
        />
      )}

      {detailOpen && selectedSupplier && (
        <SupplierDetail
          supplier={selectedSupplier}
          onClose={() => { setDetailOpen(false); setSelectedSupplier(null); }}
          onEdit={canEdit ? () => { setDetailOpen(false); setFormOpen(true); } : null}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        type="danger"
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
      />
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color }) {
  const bgMap     = { teal: 'bg-teal-50 border-teal-100', green: 'bg-green-50 border-green-100', blue: 'bg-blue-50 border-blue-100', amber: 'bg-amber-50 border-amber-100' };
  const iconBgMap = { teal: 'bg-teal-100', green: 'bg-green-100', blue: 'bg-blue-100', amber: 'bg-amber-100' };
  const iconColorMap = { teal: 'text-teal-600', green: 'text-green-600', blue: 'text-blue-600', amber: 'text-amber-600' };

  return (
    <div className={`rounded-xl border p-3.5 ${bgMap[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg ${iconBgMap[color]} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${iconColorMap[color]}`} />
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      </div>
      <p className="text-xl font-bold text-slate-800">{value}</p>
      <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>
    </div>
  );
}